import React from 'react'
import { Map, List } from 'immutable'
import _compact from 'lodash/compact'
import _uniq from 'lodash/uniq'
import _map from 'lodash/map'
import {
  getFeaturedContent,
  getFeatureMedia,
  isGenreInterview,
  isEpisode,
} from 'services/content'
import {
  TYPE_CONTENT_SERIES,
  TYPE_TAG_YOGA_STYLE,
  TYPE_TAG_YOGA_LEVEL,
  TYPE_TAG_YOGA_TEACHER,
  TYPE_TAG_FITNESS_STYLE,
  TYPE_TAG_FITNESS_LEVEL,
  TYPE_TAG_FITNESS_INSTRUCTOR,
  TYPE_TAG_MEDITATION_FOCUS,
  SUBTYPE_CONTENT_MEDITATION,
  isGuideType,
} from 'services/content-type'
import {
  formatDuration,
} from 'theme/web-app'
import Link from 'components/Link'
import TextSeasonEpisode from 'components/TextSeasonEpisode'
import Button, { TYPES } from 'components/Button'
import { ICON_TYPES } from 'components/Icon.v2'
import {
  getTileV2MobileMetadataClass,
  handleRemoveFromContinueWatching,
  handleTileV2OnClick,
} from '../utils'

/**
 * Renders the season and episode numbers
 * @param {Object} props - The props
 * @param {Number} props.seasonNumber - The season number
 * @param {Number} props.episodeNumber - The episode number
 * @param {Boolean} props.isGuide - Whether or not the content is a guide
 * @returns {String|null} - If seasonNumber and episodeNumber are null, return null.
 *  Otherwise return the formatted season and episode numbers
 */
function SeasonEpisodeNumbers (props) {
  const { seasonNumber, episodeNumber, isGuide } = props

  return (
    <TextSeasonEpisode
      className={['tile-v2__mobile-season-episode-numbers']}
      season={seasonNumber}
      episode={episodeNumber}
      isGuide={isGuide}
    />
  )
}

/**
 * Series season episode in the "Series Title: S1:E1" format
 * @param {Object} props - The props
 * @param {Number} props.seasonNumber - The season number
 * @param {Number} props.episodeNumber - The episode number
 * @returns {JSX.Element|null} Series title combined with the season and episode number
 */
function SeriesSeasonEpisode (props) {
  const {
    seasonNumber,
    episodeNumber,
    isGuide,
  } = props

  if (!seasonNumber || !episodeNumber) {
    return null
  }

  return (
    <span className="tile-v2__mobile-series-season-episode">
      <SeasonEpisodeNumbers
        seasonNumber={seasonNumber}
        episodeNumber={episodeNumber}
        isGuide={isGuide}
      />
    </span>
  )
}

function MobileMetadataTop (props) {
  const {
    data,
    featuredEpisode,
    contentType,
    userLanguage,
    isContinueWatchingV3,
  } = props
  const tags = Map.isMap(featuredEpisode) && featuredEpisode.size ? featuredEpisode.get('references', List()) : data.get('references', List()) || List()
  const yogaTeacherTag = tags.find((t) => t.get('type') === TYPE_TAG_YOGA_TEACHER && t.get('language') === userLanguage) || Map()
  const fitnessInstructorTag = tags.find((t) => t.get('type') === TYPE_TAG_FITNESS_INSTRUCTOR && t.get('language') === userLanguage) || Map()
  const showYogaFitness = yogaTeacherTag.size || fitnessInstructorTag.size
  const showMetadataLeft = showYogaFitness
    || contentType === TYPE_CONTENT_SERIES
    || isEpisode(props)

  const seriesTitle = isEpisode(props) ? data.get('seriesTitle') : data.get('title')
  const seriesUrl = isEpisode(props) ? data.get('seriesPath') : data.get('path')
  const yogaTeacher = yogaTeacherTag.get('title', '')
  const yogaTeacherUrl = yogaTeacherTag.get('path', '')
  const fitnessInstructor = fitnessInstructorTag.get('title', '')
  const fintessInstructorUrl = fitnessInstructorTag.get('path', '')
  const featuredEpisodeNumber = featuredEpisode.get('episodeNumber', 0)
  const featuredEpisodeSeasonNumber = featuredEpisode.get('seasonNumber', 0)
  const isGuide = isGuideType(data)

  const getLeftContent = () => {
    if (!showMetadataLeft) return null
    const commonLinkProps = {
      className: 'tile-v2__mobile-metadata-top-link',
      onClick: (e) => handleTileV2OnClick(e, props),
    }

    if (fitnessInstructorTag.size) {
      return <Link to={fintessInstructorUrl} {...commonLinkProps}>{fitnessInstructor}</Link>
    }

    if (yogaTeacherTag.size) {
      return <Link to={yogaTeacherUrl} {...commonLinkProps}>{yogaTeacher}</Link>
    }

    return (
      <>
        {featuredEpisode.size > 0 && (
          <SeriesSeasonEpisode
            seasonNumber={featuredEpisodeSeasonNumber}
            episodeNumber={featuredEpisodeNumber}
            isGuide={isGuide}
          />
        )}
        <Link to={seriesUrl} {...commonLinkProps}>
          {seriesTitle}
        </Link>
      </>
    )
  }

  return (
    <div className="tile-v2__mobile-metadata-top">
      {showMetadataLeft && (
        <div className={`tile-v2__mobile-metadata-top-left${isContinueWatchingV3 ? ' tile-v2__mobile-metadata-top-left--cw3' : ''}`}>
          {getLeftContent()}
        </div>
      )}
      {isContinueWatchingV3 && (
        <div className="tile-v2__mobile-metadata-top-right">
          <Button
            className="tile-v2__mobile-metadata-top-remove-btn"
            onClick={() => handleRemoveFromContinueWatching(props)}
            type={TYPES.ICON}
            icon={ICON_TYPES.CLOSE}
          />
        </div>
      )}
    </div>
  )
}

function MobileMetadataBottom (props) {
  const {
    data,
    featuredEpisode,
    userLanguage,
    staticText,
  } = props
  const contentSubtype = Map.isMap(featuredEpisode) && featuredEpisode.size ? featuredEpisode.get('contentSubtype') : data.get('contentSubtype')
  const tags = Map.isMap(featuredEpisode) && featuredEpisode.size ? featuredEpisode.get('references', List()) : data.get('references', List()) || List()
  const yogaStyleTag = tags.find((t) => t.get('type') === TYPE_TAG_YOGA_STYLE && t.get('language') === userLanguage) || Map()
  const yogaLevelTag = tags.find((t) => t.get('type') === TYPE_TAG_YOGA_LEVEL && t.get('language') === userLanguage) || Map()
  const fitnessStyleTag = tags.find((t) => t.get('type') === TYPE_TAG_FITNESS_STYLE && t.get('language') === userLanguage) || Map()
  const fitnessLevelTag = tags.find((t) => t.get('type') === TYPE_TAG_FITNESS_LEVEL && t.get('language') === userLanguage) || Map()
  const meditationFocusTag = tags.find((t) => t.get('type') === TYPE_TAG_MEDITATION_FOCUS && t.get('language') === userLanguage) || Map()
  const featuredMedia = getFeatureMedia(props)
  const duration = formatDuration(featuredMedia.get('duration', 0) || 0)
  const yogaStyleTitle = yogaStyleTag.get('title')
  const yogaLevelTitle = yogaLevelTag.get('title')
  const fitnessStyleTitle = fitnessStyleTag.get('title')
  const fitnessLevelTitle = fitnessLevelTag.get('title')
  const meditationFocusTitle = meditationFocusTag.get('title')
  const cast = Map.isMap(featuredEpisode) && featuredEpisode.size ? featuredEpisode.get('cast') : data.get('cast') || undefined
  const isInverview = isGenreInterview({ tags })

  const renderMetadataItem = (item, classModifier) => {
    let className = 'tile-v2__mobile-metadata-item'

    if (classModifier) {
      className += ` ${className}--${classModifier}`
    }

    return (
      <span className={className} key={item}>
        {item}
      </span>
    )
  }

  if (isInverview && cast) {
    return (
      <div className="tile-v2__mobile-metadata-bottom">
        {renderMetadataItem(staticText.get('with'), 'with')}
        {renderMetadataItem(cast, 'cast')}
      </div>
    )
  }

  const metadata = [duration]

  if (contentSubtype === SUBTYPE_CONTENT_MEDITATION) {
    metadata.push(staticText.get('meditation'))
  }

  metadata.push(
    yogaStyleTitle,
    yogaLevelTitle,
    fitnessStyleTitle,
    fitnessLevelTitle,
    meditationFocusTitle,
  )

  return (
    <div className="tile-v2__mobile-metadata-bottom">
      {
        _map(_uniq(_compact(metadata)), (i) => {
          return (
            renderMetadataItem(i)
          )
        })
      }
    </div>
  )
}

/**
 * The title rendered as a link
 * @param {Object} props - The props
 * @param {String} props.title - The content title
 * @param {String} props.url - The url of the content
 * @returns {JSX.Element|null} - The content title as a link
 */
function TitleLink (props) {
  const {
    title,
    url,
  } = props

  if (!title || !url) {
    return null
  }

  return (
    <Link
      to={url}
      className="tile-v2__mobile-title"
      onClick={(e) => handleTileV2OnClick(e, props)}
    >
      {title}
    </Link>
  )
}

function TileV2GraphqlMobileMetadata (props) {
  const {
    data,
    displayMode,
    isTouchable,
  } = props
  const title = data.get('title', '')
  const url = data.get('path', '')
  const featuredEpisode = getFeaturedContent(props)
  const featuredEpisodeInProgress = featuredEpisode.getIn(['featureMedia', 'userMediaPosition', 'position'], 0) > 0
  const featuredEpisodeTitle = featuredEpisode.get('title', '')
  const featuredEpisodeUrl = featuredEpisode.get('path', '')

  if (!isTouchable) {
    return null
  }

  return (
    <div className={getTileV2MobileMetadataClass({ baseClassName: 'tile-v2__mobile-metadata', displayMode })}>
      <MobileMetadataTop {...props} featuredEpisode={featuredEpisode} />
      <TitleLink
        {...props}
        url={featuredEpisodeInProgress ? featuredEpisodeUrl : url}
        title={featuredEpisodeInProgress ? featuredEpisodeTitle : title}
      />
      <MobileMetadataBottom {...props} featuredEpisode={featuredEpisode} />
    </div>
  )
}

export default TileV2GraphqlMobileMetadata
